import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { productService, categoryService, allergenService, ingredientService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { useAutoTranslate } from '../hooks/useAutoTranslate';
import Toast from '../components/common/Toast';

const API_BASE_URL = (typeof window !== 'undefined' ? `${window.location.origin}/api` : ((process.env.REACT_APP_API_URL as string | undefined) || '/api'));
interface Product {
  id: number;
  name: string;
  name_en?: string;
  description?: string;
  description_en?: string;
  price?: number;
  price_unit?: string;
  category_id?: number;
  category_name?: string;
  image_path?: string;
  is_available: boolean;
  created_at: string;
  updated_at: string;
  allergens?: Allergen[];
  ingredients?: Ingredient[];
}

interface Category {
  id: number;
  name: string;
  parent_id?: number | null;
  parent_name?: string;
  display_name?: string;
}

interface Allergen {
  id: number;
  name: string;
  icon?: string;
}

interface Ingredient {
  id: number;
  name: string;
  name_en?: string;
  icon?: string;
  allergens?: Allergen[];
  allergen_ids?: number[];
}

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BulkBar = styled.div`
  position: sticky;
  top: 8px;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fff;
  color: #0f172a;
  padding: 12px 16px;
  border: 1px solid #e7ebf0;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.08);
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  gap: 16px;
  
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  flex: 1;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
  max-width: 400px;
  
  @media (max-width: 767px) {
    max-width: none;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  
  &:hover {
    background: #f9fafb;
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const TableCell = styled.td`
  padding: 16px;
  color: #6b7280;
  font-size: 14px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const Modal = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop),
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 480px) {
    padding: 16px;
    width: 96%;
    border-radius: 8px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  
  &:hover {
    color: #374151;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const InlineFields = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;


const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  margin-right: 8px;
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
`;

const PriceDisplay = styled.span`
  font-weight: 600;
  color: #059669;
`;

const AvailabilityBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => !['available'].includes(prop),
})<{ available: boolean }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ available }) => available ? '#d1fae5' : '#fee2e2'};
  color: ${({ available }) => available ? '#065f46' : '#991b1b'};
`;

const EnglishName = styled.div`
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  margin-top: 2px;
`;



const HiddenFileInput = styled.input`
  display: none;
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  background: #fff;
  transition: all 0.2s;
  
  &:hover {
    border-color: #3b82f6;
  }
`;

const ImagePreview = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
  border-radius: 8px;
`;

const RemoveImageButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #dc2626;
  }
`;

const ProductImage = styled.img`
  width: 60px;
  height: 45px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f3f4f6;
  }
`;

const AccordionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
`;

const AccordionArrow = styled.span.withConfig({
  shouldForwardProp: (prop) => !['open'].includes(prop),
})<{ open: boolean }>`
  transition: transform 0.2s;
  transform: ${({ open }) => open ? 'rotate(0deg)' : 'rotate(-90deg)'};
  color: #6b7280;
  font-size: 12px;
`;

const SelectionBadge = styled.span`
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
`;

const Products: React.FC = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'cook';
  const { toasts, showToast } = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allergens, setAllergens] = useState<Allergen[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState<string>('all');
  const [availabilityFilter, setAvailabilityFilter] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [ingredientFilter, setIngredientFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    description: '',
    description_en: '',
    price: '',
    price_unit: '',
    category_id: '',
    is_available: true,
    allergen_ids: [] as number[],
    ingredient_ids: [] as number[]
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [currentImagePath, setCurrentImagePath] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteName, setDeleteName] = useState<string | null>(null);
  const [showMissingFieldsWarning, setShowMissingFieldsWarning] = useState(false);
  const [missingFieldsMessage, setMissingFieldsMessage] = useState('');
  const [allergenFilter, setAllergenFilter] = useState('');
  const [ingredientsOpen, setIngredientsOpen] = useState(false);
  const [allergensOpen, setAllergensOpen] = useState(false);

  const { translatedText: translatedName, isTranslating: isTranslatingName } = useAutoTranslate(
    formData.name, formData.name_en, 800
  );
  const { translatedText: translatedDesc, isTranslating: isTranslatingDesc } = useAutoTranslate(
    formData.description, formData.description_en, 1200
  );

  useEffect(() => {
    if (translatedName && !formData.name_en.trim()) {
      setFormData(prev => ({ ...prev, name_en: translatedName }));
    }
  }, [translatedName, formData.name_en]);

  useEffect(() => {
    if (translatedDesc && !formData.description_en.trim()) {
      setFormData(prev => ({ ...prev, description_en: translatedDesc }));
    }
  }, [translatedDesc, formData.description_en]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [productsRes, categoriesRes, allergensRes, ingredientsRes] = await Promise.all([
          productService.getAll(),
          categoryService.getAll(),
          allergenService.getAll(),
          ingredientService.getAll()
        ]);
        if (cancelled) return;
        setProducts(productsRes.data?.products || []);
        setCategories(categoriesRes.data?.categories || []);
        setAllergens(allergensRes.data?.allergens || []);
        setIngredients(ingredientsRes.data?.ingredients || []);
      } catch (err) {
        if (!cancelled) {
          setError('Errore nel caricamento dei dati');
          console.error('Errore nel caricamento:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const reloadProducts = async () => {
    try {
      const res = await productService.getAll();
      setProducts(res.data?.products || []);
    } catch (err) {
      console.error('Errore nel ricaricamento prodotti:', err);
    }
  };

  // Selezione multipla / modifica in gruppo
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkAddAllergens, setBulkAddAllergens] = useState<number[]>([]);
  const [bulkRemoveAllergens, setBulkRemoveAllergens] = useState<number[]>([]);
  const [bulkAddIngredients, setBulkAddIngredients] = useState<number[]>([]);
  const [bulkAvailability, setBulkAvailability] = useState<'keep' | 'available' | 'unavailable'>('keep');

  const toggleSelected = (id: number) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const searchTermLower = useMemo(() => searchTerm.toLowerCase(), [searchTerm]);

  const filteredProducts = useMemo(() => products.filter(product => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTermLower) ||
      product.category_name?.toLowerCase().includes(searchTermLower);

    if (!matchesSearch) return false;

    if (categoryFilter !== 'all') {
      const categoryIdNumber = parseInt(categoryFilter, 10);
      const childrenIds = categories
        .filter(c => c.parent_id === categoryIdNumber)
        .map(c => c.id);
      if (![categoryIdNumber, ...childrenIds].includes(product.category_id || 0)) return false;
    }

    if (subcategoryFilter !== 'all') {
      if ((product.category_id || 0) !== parseInt(subcategoryFilter, 10)) return false;
    }

    if (availabilityFilter === 'available' && !product.is_available) return false;
    if (availabilityFilter === 'unavailable' && product.is_available) return false;

    return true;
  }), [products, searchTermLower, categoryFilter, subcategoryFilter, availabilityFilter, categories]);

  const topLevelCategories = useMemo(() => categories.filter(c => !c.parent_id), [categories]);

  const availableSubcategories = useMemo(() =>
    categoryFilter === 'all'
      ? []
      : categories.filter(c => c.parent_id === parseInt(categoryFilter, 10)),
    [categories, categoryFilter]
  );

  const openModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      // Allergeni ereditati dagli ingredienti del prodotto: vanno esclusi
      // dalla lista "manuale" (restano mostrati come automatici/bloccati)
      const prodIngIds = product.ingredients?.map(i => i.id) || [];
      const autoIds = new Set<number>();
      for (const iid of prodIngIds) {
        const ing = ingredients.find(i => i.id === iid);
        (ing?.allergen_ids || (ing?.allergens || []).map(a => a.id)).forEach(a => autoIds.add(a));
      }
      setFormData({
        name: product.name,
        name_en: product.name_en || '',
        description: product.description || '',
        description_en: product.description_en || '',
        price: product.price?.toString() || '',
        price_unit: product.price_unit || '',
        category_id: product.category_id?.toString() || '',
        is_available: product.is_available,
        allergen_ids: (product.allergens?.map(a => a.id) || []).filter(id => !autoIds.has(id)),
        ingredient_ids: prodIngIds
      });
      setCurrentImagePath(product.image_path || null);
      setImagePreview(null);
      setSelectedImage(null);
      setIngredientFilter('');
      setAllergenFilter('');
      setIngredientsOpen((product.ingredients?.length || 0) > 0);
      setAllergensOpen((product.allergens?.length || 0) > 0);
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        name_en: '',
        description: '',
        description_en: '',
        price: '',
        price_unit: '',
        category_id: '',
        is_available: true,
        allergen_ids: [],
        ingredient_ids: []
      });
      setCurrentImagePath(null);
      setImagePreview(null);
      setSelectedImage(null);
      setIngredientFilter('');
      setAllergenFilter('');
      setIngredientsOpen(false);
      setAllergensOpen(false);
    }
    setFormError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setFormError(null);
    setSelectedImage(null);
    setImagePreview(null);
    setCurrentImagePath(null);
  };

  const executeSubmit = async () => {
    try {
      setSubmitting(true);
      setFormError(null);

      // Creo FormData per gestire il file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      
      if (formData.name_en.trim()) {
        formDataToSend.append('name_en', formData.name_en.trim());
      }
      
      if (formData.description && formData.description.trim()) {
        formDataToSend.append('description', formData.description.trim());
      }

      if (formData.description_en && formData.description_en.trim()) {
        formDataToSend.append('description_en', formData.description_en.trim());
      }
      
      if (formData.price) {
        formDataToSend.append('price', formData.price);
      }
      // Invia sempre price_unit: stringa vuota indica rimozione
      formDataToSend.append('price_unit', formData.price_unit || '');
      
      if (formData.category_id) {
        formDataToSend.append('category_id', formData.category_id);
      }
      
      formDataToSend.append('is_available', formData.is_available.toString());
      
      // Aggiungo gli allergeni
      formData.allergen_ids.forEach(id => {
        formDataToSend.append('allergen_ids[]', id.toString());
      });
      
      // Aggiungo gli ingredienti
      formData.ingredient_ids.forEach(id => {
        formDataToSend.append('ingredient_ids[]', id.toString());
      });
      
      // Aggiungo l'immagine se selezionata
      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      const enrich = (raw: any): Product => ({
        ...raw,
        is_available: Boolean(raw.is_available),
        allergens: allergens.filter(a => formData.allergen_ids.includes(a.id)),
        ingredients: ingredients.filter(i => formData.ingredient_ids.includes(i.id)),
      });

      if (editingProduct) {
        const res = await productService.update(editingProduct.id, formDataToSend);
        setProducts(prev => prev.map(p => p.id === editingProduct.id ? enrich(res.data.product) : p));
        showToast('Prodotto aggiornato');
      } else {
        const res = await productService.create(formDataToSend);
        setProducts(prev => [...prev, enrich(res.data.product)]);
        showToast('Prodotto creato');
      }

      closeModal();
      setShowMissingFieldsWarning(false);
    } catch (err: any) {
      console.error('Errore nel salvataggio:', err);
      setFormError(err.response?.data?.message || 'Errore nel salvataggio del prodotto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setFormError('Il nome è obbligatorio');
      return;
    }

    // Check for missing price or category only for new products (or always? "quando inserisco un nuovo prodotto")
    // User said "when I insert a new product", but it might be useful for edits too.
    // However, to be safe and strictly follow "quando inserisco", I can check if !editingProduct.
    // But usually these checks are good for edits too if the user clears the field.
    // Let's apply it generally but focus on the "empty" state.

    const isPriceMissing = !formData.price || formData.price.trim() === '' || parseFloat(formData.price) === 0;
    const isCategoryMissing = !formData.category_id || formData.category_id === '';

    if (isPriceMissing || isCategoryMissing) {
      const missingParts = [];
      if (isPriceMissing) missingParts.push('prezzo');
      if (isCategoryMissing) missingParts.push('categoria');
      
      const message = `Sei sicuro di voler creare il prodotto senza ${missingParts.join(' e ')}?`;
      setMissingFieldsMessage(message);
      setShowMissingFieldsWarning(true);
      return;
    }

    await executeSubmit();
  };

  const handleDeleteConfirmed = async () => {
    if (deleteId == null) return;
    const backup = products;
    const idToDelete = deleteId;
    setProducts(prev => prev.filter(p => p.id !== idToDelete));
    setDeleteId(null);
    setDeleteName(null);
    try {
      await productService.delete(idToDelete);
      showToast('Prodotto eliminato');
    } catch (err: any) {
      setProducts(backup);
      setError(err.response?.data?.message || 'Errore nell\'eliminazione del prodotto');
      showToast('Errore nell\'eliminazione', 'error');
    }
  };

  const openBulk = () => {
    setBulkAddAllergens([]);
    setBulkRemoveAllergens([]);
    setBulkAddIngredients([]);
    setBulkAvailability('keep');
    setBulkOpen(true);
  };

  const handleBulkApply = async () => {
    if (selectedIds.size === 0) return;
    setBulkSubmitting(true);
    try {
      await productService.bulkUpdate({
        product_ids: Array.from(selectedIds),
        add_allergen_ids: bulkAddAllergens,
        remove_allergen_ids: bulkRemoveAllergens,
        add_ingredient_ids: bulkAddIngredients,
        is_available: bulkAvailability === 'keep' ? undefined : bulkAvailability === 'available',
      });
      await reloadProducts();
      showToast(`${selectedIds.size} prodotti aggiornati`);
      setBulkOpen(false);
      setSelectedIds(new Set());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Errore nella modifica in gruppo');
      showToast('Errore nella modifica in gruppo', 'error');
    } finally {
      setBulkSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isModalOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isModalOpen]);


  const handleAllergenChange = (allergenId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      allergen_ids: checked
        ? [...prev.allergen_ids, allergenId]
        : prev.allergen_ids.filter(id => id !== allergenId)
    }));
  };

  // Allergeni ereditati automaticamente dagli ingredienti selezionati
  const autoAllergenIds = new Set<number>();
  for (const ingId of formData.ingredient_ids) {
    const ing = ingredients.find(i => i.id === ingId);
    const ids = ing?.allergen_ids || (ing?.allergens || []).map(a => a.id);
    for (const aid of ids) autoAllergenIds.add(aid);
  }

  const handleIngredientChange = (ingredientId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      ingredient_ids: checked
        ? [...prev.ingredient_ids, ingredientId]
        : prev.ingredient_ids.filter(id => id !== ingredientId)
    }));
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Verifica che sia un'immagine
      if (!file.type.startsWith('image/')) {
        setFormError('Seleziona un file immagine valido');
        return;
      }
      
      // Verifica dimensione (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setFormError('L\'immagine deve essere inferiore a 5MB');
        return;
      }
      
      setSelectedImage(file);
      
      // Crea preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setCurrentImagePath(null);
    const gallery = document.getElementById('image') as HTMLInputElement | null;
    const camera = document.getElementById('image-camera') as HTMLInputElement | null;
    if (gallery) gallery.value = '';
    if (camera) camera.value = '';
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Caricamento prodotti...</LoadingState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Toast toasts={toasts} />
      <PageHeader>
        <Title>Gestione Prodotti</Title>
        <SearchContainer>
          <Input
            type="text"
            placeholder="Cerca per nome o categoria..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {canEdit && (
            <Button onClick={() => openModal()}>
              Aggiungi Prodotto
            </Button>
          )}
        </SearchContainer>
      </PageHeader>

      <FiltersRow>
        <FormGroup>
          <Label>Categoria</Label>
          <Select
            value={categoryFilter}
            onChange={(e) => {
              const value = e.target.value;
              setCategoryFilter(value);
              setSubcategoryFilter('all');
            }}
          >
            <option value="all">Tutte le categorie</option>
            {topLevelCategories.map(category => (
              <option key={category.id} value={category.id.toString()}>
                {category.name}
              </option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Sottocategoria</Label>
          <Select
            value={subcategoryFilter}
            onChange={(e) => setSubcategoryFilter(e.target.value)}
            disabled={categoryFilter === 'all' || availableSubcategories.length === 0}
          >
            <option value="all">Tutte le sottocategorie</option>
            {availableSubcategories.map(sub => (
              <option key={sub.id} value={sub.id.toString()}>
                {sub.name}
              </option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Disponibilità</Label>
          <Select
            value={availabilityFilter}
            onChange={(e) => setAvailabilityFilter(e.target.value)}
          >
            <option value="all">Tutti</option>
            <option value="available">Nel menu</option>
            <option value="unavailable">Fuori menu</option>
          </Select>
        </FormGroup>
      </FiltersRow>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {canEdit && selectedIds.size > 0 && (
        <BulkBar>
          <span><strong>{selectedIds.size}</strong> selezionati</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="small" onClick={openBulk}>Modifica in gruppo</Button>
            <Button size="small" variant="secondary" onClick={() => setSelectedIds(new Set())}>Deseleziona</Button>
          </div>
        </BulkBar>
      )}

      <Card>
        {filteredProducts.length === 0 ? (
          <EmptyState>
            {searchTerm ? 'Nessun prodotto trovato per la ricerca.' : 'Nessun prodotto presente. Aggiungi il primo prodotto!'}
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {canEdit && (
                  <TableHeaderCell style={{ width: 44 }}>
                    <Checkbox
                      checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedIds.has(p.id))}
                      onChange={(e) => {
                        const visibleIds = filteredProducts.map(p => p.id);
                        setSelectedIds(prev => {
                          const n = new Set(prev);
                          if (e.target.checked) visibleIds.forEach(id => n.add(id));
                          else visibleIds.forEach(id => n.delete(id));
                          return n;
                        });
                      }}
                    />
                  </TableHeaderCell>
                )}
                <TableHeaderCell>Immagine</TableHeaderCell>
                <TableHeaderCell>Nome</TableHeaderCell>
                <TableHeaderCell>Categoria</TableHeaderCell>
                <TableHeaderCell>Prezzo</TableHeaderCell>
                <TableHeaderCell>Disponibilità</TableHeaderCell>
                <TableHeaderCell>Azioni</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {filteredProducts.map((product) => (
                <TableRow key={product.id}>
                  {canEdit && (
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(product.id)}
                        onChange={() => toggleSelected(product.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {product.image_path ? (
                      <ProductImage 
                        src={`${API_BASE_URL}${product.image_path}`}
                        alt={product.name}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '60px', 
                        height: '45px', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: '#9ca3af'
                      }}>
                        No img
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <strong>{product.name}</strong>
                      {product.name_en && (
                        <EnglishName>{product.name_en}</EnglishName>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {product.category_name || '-'}
                  </TableCell>
                  <TableCell>
                    {product.price ? (
                      <PriceDisplay>
                        €{product.price.toFixed(2)}{product.price_unit ? ` / ${product.price_unit}` : ''}
                      </PriceDisplay>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <AvailabilityBadge available={product.is_available}>
                      {product.is_available ? 'Disponibile' : 'Non disponibile'}
                    </AvailabilityBadge>
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <ActionButtons>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => openModal(product)}
                        >
                          Modifica
                        </Button>
                        <Button
                          variant="danger"
                          size="small"
                          onClick={() => {
                            setDeleteId(product.id);
                            setDeleteName(product.name);
                          }}
                        >
                          Elimina
                        </Button>
                      </ActionButtons>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
            </ModalTitle>
            <CloseButton onClick={closeModal}>&times;</CloseButton>
          </ModalHeader>

          {formError && <ErrorMessage>{formError}</ErrorMessage>}

          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome del prodotto"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="name_en">Nome (Inglese)</Label>
                <Input
                  id="name_en"
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  placeholder={
                    isTranslatingName
                      ? 'Traduzione in corso...'
                      : translatedName
                        ? `Suggerimento: ${translatedName}`
                        : 'Nome in inglese (o lascia vuoto per traduzione automatica)'
                  }
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label htmlFor="description">Descrizione</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione del prodotto (opzionale)"
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="description_en">Descrizione (Inglese)</Label>
                <textarea
                  id="description_en"
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  placeholder={
                    isTranslatingDesc
                      ? 'Traduzione in corso...'
                      : translatedDesc
                        ? `Suggerimento: ${translatedDesc}`
                        : 'Descrizione in inglese (o lascia vuoto per traduzione automatica)'
                  }
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label htmlFor="category_id">Categoria</Label>
                <Select
                  id="category_id"
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                >
                    <option value="">Seleziona categoria</option>
                    {categories
                      .slice()
                      .sort((a, b) => {
                        const nameA = a.parent_name ? `${a.parent_name} -- ${a.name}` : a.name;
                        const nameB = b.parent_name ? `${b.parent_name} -- ${b.name}` : b.name;
                        return nameA.localeCompare(nameB, 'it', { sensitivity: 'base' });
                      })
                      .map(category => (
                      <option key={category.id} value={category.id}>
                      {category.parent_name ? `${category.parent_name} -- ${category.name}` : category.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="price">Prezzo (€)</Label>
                <InlineFields>
                  <input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <Select
                    id="price_unit"
                    value={formData.price_unit}
                    onChange={(e) => setFormData({ ...formData, price_unit: e.target.value })}
                    style={{ width: '140px' }}
                  >
                    <option value="">Nessuna unità</option>
                    <option value="g">g (grammo)</option>
                    <option value="hg">hg (etto)</option>
                    <option value="l">l (litro)</option>
                  </Select>
                </InlineFields>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label htmlFor="image">Immagine prodotto</Label>
              <HiddenFileInput
                id="image"
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
              />
              <HiddenFileInput
                id="image-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageSelect}
              />

              {(imagePreview || currentImagePath) ? (
                <div style={{ textAlign: 'center' }}>
                  <PreviewImage
                    src={imagePreview || `${API_BASE_URL}${currentImagePath}`}
                    alt="Anteprima immagine"
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
                    <label htmlFor="image">
                      <Button type="button" variant="secondary" as="span" style={{ cursor: 'pointer' }}>Galleria</Button>
                    </label>
                    <label htmlFor="image-camera">
                      <Button type="button" variant="secondary" as="span" style={{ cursor: 'pointer' }}>📷 Fotocamera</Button>
                    </label>
                    <Button type="button" variant="danger" onClick={handleRemoveImage}>Rimuovi</Button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <label htmlFor="image" style={{ flex: 1 }}>
                    <ImageUploadArea>
                      <span style={{ fontSize: 13, color: '#6b7280' }}>📁 Scegli dalla galleria</span>
                    </ImageUploadArea>
                  </label>
                  <label htmlFor="image-camera" style={{ flex: 1 }}>
                    <ImageUploadArea>
                      <span style={{ fontSize: 13, color: '#6b7280' }}>📷 Scatta una foto</span>
                    </ImageUploadArea>
                  </label>
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <AccordionHeader onClick={() => setIngredientsOpen(o => !o)}>
                <AccordionTitle>
                  Ingredienti
                  {formData.ingredient_ids.length > 0 && (
                    <SelectionBadge>{formData.ingredient_ids.length} selezionati</SelectionBadge>
                  )}
                </AccordionTitle>
                <AccordionArrow open={ingredientsOpen}>▾</AccordionArrow>
              </AccordionHeader>
              {ingredientsOpen && (
                <>
                  <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                    <Input
                      type="text"
                      placeholder="Cerca ingrediente..."
                      value={ingredientFilter}
                      onChange={(e) => setIngredientFilter(e.target.value)}
                    />
                  </div>
                  <CheckboxGroup>
                    {ingredients
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }))
                      .filter(ingredient => {
                        if (!ingredientFilter.trim()) return true;
                        const search = ingredientFilter.toLowerCase();
                        return ingredient.name.toLowerCase().includes(search) ||
                          (ingredient.name_en || '').toLowerCase().includes(search);
                      })
                      .map(ingredient => (
                        <CheckboxLabel key={ingredient.id}>
                          <Checkbox
                            checked={formData.ingredient_ids.includes(ingredient.id)}
                            onChange={(e) => handleIngredientChange(ingredient.id, e.target.checked)}
                          />
                          {ingredient.icon} {ingredient.name}
                        </CheckboxLabel>
                      ))}
                  </CheckboxGroup>
                </>
              )}
            </FormGroup>

            <FormGroup>
              <AccordionHeader onClick={() => setAllergensOpen(o => !o)}>
                <AccordionTitle>
                  Allergeni
                  {(() => {
                    const total = new Set([...formData.allergen_ids, ...Array.from(autoAllergenIds)]).size;
                    return total > 0 ? <SelectionBadge>{total} totali</SelectionBadge> : null;
                  })()}
                </AccordionTitle>
                <AccordionArrow open={allergensOpen}>▾</AccordionArrow>
              </AccordionHeader>
              {allergensOpen && (
                <>
                  <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                    <Input
                      type="text"
                      placeholder="Cerca allergene..."
                      value={allergenFilter}
                      onChange={(e) => setAllergenFilter(e.target.value)}
                    />
                  </div>
                  {autoAllergenIds.size > 0 && (
                    <div style={{ fontSize: '12px', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 10px', marginBottom: '8px' }}>
                      🔒 Gli allergeni con lucchetto arrivano in automatico dagli ingredienti e non si possono togliere qui. Puoi aggiungerne altri a mano (es. tracce).
                    </div>
                  )}
                  <CheckboxGroup>
                    {allergens
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }))
                      .filter(allergen =>
                        !allergenFilter.trim() ||
                        allergen.name.toLowerCase().includes(allergenFilter.toLowerCase())
                      )
                      .map(allergen => {
                        const isAuto = autoAllergenIds.has(allergen.id);
                        return (
                          <CheckboxLabel key={allergen.id} style={isAuto ? { opacity: 0.85 } : undefined}>
                            <Checkbox
                              checked={isAuto || formData.allergen_ids.includes(allergen.id)}
                              disabled={isAuto}
                              onChange={(e) => handleAllergenChange(allergen.id, e.target.checked)}
                            />
                            {allergen.icon} {allergen.name}
                            {isAuto && <span style={{ marginLeft: 6, fontSize: 11, color: '#b45309' }}>🔒 auto</span>}
                          </CheckboxLabel>
                        );
                      })}
                  </CheckboxGroup>
                </>
              )}
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  checked={formData.is_available}
                  onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
                />
                Prodotto disponibile
              </CheckboxLabel>
            </FormGroup>

            <FormActions>
              <Button
                type="button"
                variant="secondary"
                onClick={closeModal}
                disabled={submitting}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={submitting}
              >
                {submitting ? 'Salvataggio...' : (editingProduct ? 'Aggiorna' : 'Crea')}
              </Button>
            </FormActions>
          </Form>
        </ModalContent>
      </Modal>
      <ConfirmDialog
        isOpen={deleteId != null}
        title="Elimina prodotto"
        message={
          deleteName
            ? `Sei sicuro di voler eliminare il prodotto "${deleteName}"?`
            : 'Sei sicuro di voler eliminare questo prodotto?'
        }
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => {
          setDeleteId(null);
          setDeleteName(null);
        }}
      />
      <ConfirmDialog
        isOpen={showMissingFieldsWarning}
        title="Conferma creazione"
        message={missingFieldsMessage}
        confirmLabel="Crea comunque"
        cancelLabel="Annulla"
        onConfirm={executeSubmit}
        onCancel={() => setShowMissingFieldsWarning(false)}
      />

      {/* Modale modifica in gruppo */}
      <Modal isOpen={bulkOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Modifica in gruppo — {selectedIds.size} prodotti</ModalTitle>
            <CloseButton onClick={() => setBulkOpen(false)}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={(e) => { e.preventDefault(); handleBulkApply(); }}>
            <FormGroup>
              <Label>➕ Aggiungi allergeni a tutti</Label>
              <CheckboxGroup>
                {allergens.slice().sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })).map(a => (
                  <CheckboxLabel key={a.id}>
                    <Checkbox
                      checked={bulkAddAllergens.includes(a.id)}
                      onChange={(e) => setBulkAddAllergens(prev => e.target.checked ? [...prev, a.id] : prev.filter(x => x !== a.id))}
                    />
                    {a.icon} {a.name}
                  </CheckboxLabel>
                ))}
              </CheckboxGroup>
            </FormGroup>

            <FormGroup>
              <Label>➖ Rimuovi allergeni da tutti</Label>
              <CheckboxGroup>
                {allergens.slice().sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })).map(a => (
                  <CheckboxLabel key={a.id}>
                    <Checkbox
                      checked={bulkRemoveAllergens.includes(a.id)}
                      onChange={(e) => setBulkRemoveAllergens(prev => e.target.checked ? [...prev, a.id] : prev.filter(x => x !== a.id))}
                    />
                    {a.icon} {a.name}
                  </CheckboxLabel>
                ))}
              </CheckboxGroup>
            </FormGroup>

            <FormGroup>
              <Label>🧂 Aggiungi ingredienti a tutti (gli allergeni dell'ingrediente vengono inclusi in automatico)</Label>
              <CheckboxGroup>
                {ingredients.slice().sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })).map(ing => (
                  <CheckboxLabel key={ing.id}>
                    <Checkbox
                      checked={bulkAddIngredients.includes(ing.id)}
                      onChange={(e) => setBulkAddIngredients(prev => e.target.checked ? [...prev, ing.id] : prev.filter(x => x !== ing.id))}
                    />
                    {ing.name}
                  </CheckboxLabel>
                ))}
              </CheckboxGroup>
            </FormGroup>

            <FormGroup>
              <Label>Disponibilità</Label>
              <CheckboxGroup>
                <CheckboxLabel><input type="radio" name="bulkAvail" checked={bulkAvailability === 'keep'} onChange={() => setBulkAvailability('keep')} /> Non modificare</CheckboxLabel>
                <CheckboxLabel><input type="radio" name="bulkAvail" checked={bulkAvailability === 'available'} onChange={() => setBulkAvailability('available')} /> Rendi disponibili</CheckboxLabel>
                <CheckboxLabel><input type="radio" name="bulkAvail" checked={bulkAvailability === 'unavailable'} onChange={() => setBulkAvailability('unavailable')} /> Rendi non disponibili</CheckboxLabel>
              </CheckboxGroup>
            </FormGroup>

            <FormActions>
              <Button type="button" variant="secondary" onClick={() => setBulkOpen(false)} disabled={bulkSubmitting}>Annulla</Button>
              <Button type="submit" disabled={bulkSubmitting}>
                {bulkSubmitting ? 'Applico...' : `Applica a ${selectedIds.size} prodotti`}
              </Button>
            </FormActions>
          </Form>
        </ModalContent>
      </Modal>
    </PageContainer>
  );
};

export default Products;
